import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, BarChart2, FileText } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { reportAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const ReportsPage = () => {
  const [tab, setTab] = useState('revenue');
  const [revenueData, setRevenueData] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [revRes, occRes] = await Promise.all([
          reportAPI.getRevenue({ groupBy, startDate, endDate }),
          reportAPI.getOccupancy({ year: new Date().getFullYear() })
        ]);
        setRevenueData(revRes.data.data.propertyRevenue || []);
        setOccupancyData(occRes.data.data || []);
      } catch {
        toast.error('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [groupBy, startDate, endDate]);

  const totalRevenue = revenueData.reduce((s, d) => s + (d.revenue || 0), 0);
  const totalBookings = revenueData.reduce((s, d) => s + (d.bookings || 0), 0);
  const avgBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Revenue, occupancy, and performance insights</p>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString()}`, icon: DollarSign, color: '#059669', bg: '#ECFDF5' },
          { label: 'Total Bookings', value: totalBookings, icon: BarChart2, color: '#2563EB', bg: '#EFF6FF' },
          { label: 'Avg Booking Value', value: `$${avgBookingValue.toFixed(0)}`, icon: TrendingUp, color: '#D97706', bg: '#FFFBEB' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div className="stat-card" key={label}>
            <div className="stat-icon" style={{ background: bg }}><Icon size={22} color={color} /></div>
            <div><div className="stat-value">{value}</div><div className="stat-label">{label}</div></div>
          </div>
        ))}
      </div>

      <div className="tabs">
        {[['revenue', 'Revenue'], ['occupancy', 'Occupancy'], ['bookings', 'Bookings']].map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: 20 }}>
        <select className="form-select" style={{ width: 140 }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
          <option value="day">Daily</option>
          <option value="month">Monthly</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="date" className="form-input" style={{ width: 160 }} value={startDate} onChange={e => setStartDate(e.target.value)} />
          <span style={{ color: '#9CA3AF' }}>—</span>
          <input type="date" className="form-input" style={{ width: 160 }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        {(startDate || endDate) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <>
          {tab === 'revenue' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="card">
                <div className="card-header"><span className="card-title">Revenue Over Time</span></div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => [`$${v.toLocaleString()}`, 'Revenue']} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2.5} dot={false} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Booking Volume</span></div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="bookings" fill="#10B981" radius={[4, 4, 0, 0]} name="Bookings" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {tab === 'occupancy' && (
            <div className="card">
              <div className="card-header"><span className="card-title">Occupancy by Property</span></div>
              {occupancyData.length === 0 ? (
                <div className="empty-state"><BarChart2 size={40} /><h3>No occupancy data</h3></div>
              ) : (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={occupancyData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                        <YAxis type="category" dataKey="title" tick={{ fontSize: 12 }} width={140} />
                        <Tooltip formatter={v => [`${v}%`, 'Occupancy']} />
                        <Bar dataKey="occupancyRate" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr><th>Property</th><th>Bookings</th><th>Nights Booked</th><th>Occupancy Rate</th><th>Revenue</th></tr>
                      </thead>
                      <tbody>
                        {occupancyData.map((p, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{p.title}</td>
                            <td>{p.bookings}</td>
                            <td>{p.nights}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ width: `${p.occupancyRate}%`, height: '100%', background: '#3B82F6', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 600, width: 40 }}>{p.occupancyRate}%</span>
                              </div>
                            </td>
                            <td style={{ fontWeight: 600 }}>${p.revenue?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'bookings' && (
            <div className="card">
              <div className="card-header"><span className="card-title">Booking Trends</span></div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="bookings" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Bookings" />
                  <Line type="monotone" dataKey="avgBookingValue" stroke="#F59E0B" name="Avg Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
